import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'

import { FlexDiv } from '../../components'
import { AppRoutes } from '../../helpers/Routes'
import { getShortFormDate } from '../../helpers/stringHelpers'
import { useInspectionHub } from '../../hooks/useInspectionHub'
import { RootState } from '../../store'
import { InspectionAreaResults } from './InspectionAreaResults'
import { InspectionDetailShell } from './InspectionDetailShell'
import { MoveOutDamagesSection } from './MoveOutDamagesSection'
import { MoveOutVideosSection } from './MoveOutVideosSection'
import { countAreas, getLatestCompletedDate } from './inspectionUtils'

export const MoveOutInspectionDetail = () => {
    const navigate = useNavigate()
    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const leaseId = selectedLease?.id
    const apartmentId = selectedLease?.apartment_id

    const { hubData, isLoading, getInspectionHub } = useInspectionHub(
        leaseId,
        apartmentId,
    )

    useEffect(() => {
        if (leaseId && apartmentId) {
            getInspectionHub()
        }
    }, [leaseId, apartmentId])

    const handleBack = () => navigate(AppRoutes.inspections)

    if (!leaseId || !apartmentId) {
        return (
            <InspectionDetailShell title="Move-Out Inspection" onBack={handleBack}>
                <Typography color="textSecondary">
                    Select a lease to view inspection results.
                </Typography>
            </InspectionDetailShell>
        )
    }

    if (isLoading && !hubData) {
        return (
            <InspectionDetailShell title="Move-Out Inspection" onBack={handleBack}>
                <FlexDiv style={{ justifyContent: 'center', padding: 32, width: '100%' }}>
                    <CircularProgress size={40} />
                </FlexDiv>
            </InspectionDetailShell>
        )
    }

    const moveOut = hubData?.move_out
    if (!moveOut || moveOut.state !== 'complete') {
        return (
            <InspectionDetailShell title="Move-Out Inspection" onBack={handleBack}>
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                    Move-out inspection results are not available yet.
                </Typography>
            </InspectionDetailShell>
        )
    }

    const completedDate = getLatestCompletedDate(moveOut.detail)
    const metadata: string[] = []
    if (completedDate) {
        metadata.push(`Completed ${getShortFormDate(completedDate)}`)
    }
    metadata.push(
        `${countAreas(moveOut.detail)} area${countAreas(moveOut.detail) === 1 ? '' : 's'} inspected`,
    )

    return (
        <InspectionDetailShell
            title="Move-Out Inspection"
            status="complete"
            metadata={metadata}
            onBack={handleBack}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <MoveOutDamagesSection
                    damages={moveOut.damages ?? []}
                    totalDamages={moveOut.total_damages ?? '0.00'}
                />

                <MoveOutVideosSection inspections={moveOut.detail} />

                {moveOut.fmo_available && (
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<ExitToAppIcon />}
                        onClick={() => navigate(AppRoutes.moveOut)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                        }}
                    >
                        View move-out statement
                    </Button>
                )}

                <InspectionAreaResults
                    inspections={moveOut.detail}
                    showVideos={false}
                    defaultTab="all"
                    hideNotableTab
                />
            </Box>
        </InspectionDetailShell>
    )
}
